import React from "react";
import { TextField } from "@mui/material";
import LabeledField from "../../components/form/LabeledField";
import { PifDetailsValues } from "./pifDetailsModel";

/** `pif.firstName` -> `pif-firstName`: a stable control id per field key. */
export const pifControlId = (fieldKey: string) => fieldKey.replace(".", "-");

interface WorkflowPifTextFieldProps {
  fieldKey: string;
  label: string;
  values: PifDetailsValues;
  errors: Record<string, string>;
  onChange: (fieldKey: string, value: string) => void;
  required?: boolean;
  /** Read-only with a "Locked" pill (an identity carried over from the employee record). */
  locked?: boolean;
  hint?: string;
  hintTone?: "success" | "warning";
  type?: "text" | "number";
  inputMode?: "numeric" | "decimal" | "tel" | "email";
  multiline?: boolean;
  maxLength?: number;
}

/** One pif-3 text input, keyed by its field key. */
const WorkflowPifTextField: React.FC<WorkflowPifTextFieldProps> = ({
  fieldKey,
  label,
  values,
  errors,
  onChange,
  required = false,
  locked = false,
  hint,
  hintTone,
  type = "text",
  inputMode,
  multiline = false,
  maxLength,
}) => {
  const id = pifControlId(fieldKey);
  const error = errors[fieldKey];
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => onChange(fieldKey, event.target.value);

  return (
    <LabeledField controlId={id} label={label} required={required} badge={locked ? "Locked" : undefined} error={error} hint={hint} hintTone={hintTone}>
      <TextField
        id={id}
        size="small"
        fullWidth
        type={type}
        required={required}
        value={values[fieldKey] ?? ""}
        disabled={locked}
        error={Boolean(error)}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        onChange={handleChange}
        inputProps={{ inputMode, maxLength, step: type === "number" ? "any" : undefined }}
      />
    </LabeledField>
  );
};

export default WorkflowPifTextField;
